import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import { cn } from "@/lib/utils";

type MentionCandidate = {
  userId: string;
  email: string;
  displayName: string | null;
};

function mentionLabel(user: MentionCandidate): string {
  const name = user.displayName?.trim() ?? "";
  return name !== "" ? name : user.email;
}

function activeMentionQuery(value: string, cursor: number): { start: number; query: string } | null {
  const before = value.slice(0, cursor);
  const match = /(?:^|\s)@([^\s@]*)$/.exec(before);
  if (match == null || match.index == null) {
    return null;
  }
  const atIndex = before.lastIndexOf("@");
  if (atIndex < 0) {
    return null;
  }
  return { start: atIndex, query: match[1] ?? "" };
}

export function MentionTextarea({
  id,
  value,
  onChange,
  disabled = false,
  rows = 3,
  className,
  placeholder,
  onKeyDown,
}: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  rows?: number;
  className?: string;
  placeholder?: string;
  onKeyDown?: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
}): JSX.Element {
  const listId = useId();
  const fieldRef = useRef<HTMLTextAreaElement>(null);
  const [cursor, setCursor] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const mention = disabled ? null : activeMentionQuery(value, cursor);
  const candidatesQuery = useQuery<{ users: MentionCandidate[] }>({
    queryKey: ["notifications", "mention-quick-search", mention?.query ?? ""],
    queryFn: () =>
      apiFetch<{ users: MentionCandidate[] }>(
        `/api/notifications/mention-quick-search?q=${encodeURIComponent(mention?.query ?? "")}`,
      ),
    enabled: mention != null,
    staleTime: 30_000,
  });
  const users = mention != null ? (candidatesQuery.data?.users ?? []) : [];
  const open = mention != null && users.length > 0;

  useEffect(() => {
    setActiveIndex(0);
  }, [mention?.query]);

  function rememberCursor(nextCursor: number): void {
    setCursor(nextCursor);
  }

  function applyMention(user: MentionCandidate): void {
    if (mention == null) {
      return;
    }
    const label = `@${mentionLabel(user)} `;
    const next = `${value.slice(0, mention.start)}${label}${value.slice(cursor)}`;
    onChange(next);
    const nextCursor = mention.start + label.length;
    setCursor(nextCursor);
    requestAnimationFrame(() => {
      const field = fieldRef.current;
      if (field == null) {
        return;
      }
      field.focus();
      field.setSelectionRange(nextCursor, nextCursor);
    });
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>): void {
    if (open) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setActiveIndex((index) => (index + 1) % users.length);
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setActiveIndex((index) => (index - 1 + users.length) % users.length);
        return;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        const user = users[activeIndex];
        if (user != null) {
          event.preventDefault();
          applyMention(user);
          return;
        }
      }
      if (event.key === "Escape") {
        event.preventDefault();
        setCursor(value.length);
        return;
      }
    }
    onKeyDown?.(event);
  }

  return (
    <div className="relative">
      <textarea
        ref={fieldRef}
        id={id}
        rows={rows}
        disabled={disabled}
        value={value}
        placeholder={placeholder}
        className={className}
        role="combobox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-autocomplete="list"
        onChange={(event) => {
          onChange(event.target.value);
          rememberCursor(event.target.selectionStart ?? event.target.value.length);
        }}
        onClick={(event) => rememberCursor(event.currentTarget.selectionStart ?? value.length)}
        onKeyUp={(event) => rememberCursor(event.currentTarget.selectionStart ?? value.length)}
        onKeyDown={handleKeyDown}
      />
      {open ? (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg"
        >
          {users.map((user, index) => {
            const label = mentionLabel(user);
            const selected = index === activeIndex;
            return (
              <li key={user.userId} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={cn(
                    "flex w-full flex-col items-start px-3 py-2 text-left text-sm",
                    selected ? "bg-slate-100" : "hover:bg-slate-50",
                  )}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    applyMention(user);
                  }}
                >
                  <span className="font-medium text-slate-900">{label}</span>
                  {label !== user.email ? <span className="text-xs text-slate-500">{user.email}</span> : null}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
